import { merchandisingRegexError } from './regexValidation';

let conditionSequence = 0;
const nextId = () => {
  conditionSequence += 1;
  return `condition-${conditionSequence}`;
};

const decorateItem = (item) => {
  if (item && Array.isArray(item.items)) {
    return {
      __id: nextId(),
      match: item.match === 'any' ? 'any' : 'all',
      items: item.items.map(decorateItem),
    };
  }
  return { ...item, __id: nextId() };
};

export const decorateConditions = (conditions = { match: 'all', items: [] }) => ({
  __id: 'root',
  match: conditions.match === 'any' ? 'any' : 'all',
  items: (conditions.items || []).map(decorateItem),
});

export const stripConditions = (group) => ({
  match: group.match === 'any' ? 'any' : 'all',
  items: (group.items || []).map((item) => (
    Array.isArray(item.items)
      ? stripConditions(item)
      : { field: item.field, operator: item.operator, value: item.value }
  )),
});

export const emptyRankingWorkbench = () => ({
  id: null,
  revision: null,
  status: 'draft',
  name: '',
  note: '',
  conditions: decorateConditions(),
  factor: 2,
  boostedTermsText: '',
  midTermsText: '',
  validFrom: null,
  validTo: null,
});

export const rankingRuleToWorkbench = (rule) => ({
  id: String(rule.id),
  revision: Number(rule.revision),
  status: rule.status,
  name: String(rule.name || '').trim().toLowerCase() === 'product ranking rule'
    ? ''
    : (rule.name || ''),
  note: rule.description || '',
  conditions: decorateConditions(rule.conditions),
  factor: Number((rule.actions || []).find((action) => action.type === 'score.multiply')?.value || 1),
  boostedTermsText: (rule.actions || []).find((action) => (
    action.type === 'search_terms.add' && action.priority === 'boosted'
  ))?.terms?.join('\n') || '',
  midTermsText: (rule.actions || []).find((action) => (
    action.type === 'search_terms.add' && action.priority === 'mid'
  ))?.terms?.join('\n') || '',
  validFrom: rule.valid_from || null,
  validTo: rule.valid_to || null,
});

export const parseRankingSearchTerms = (text = '') => text.split(/\r?\n/)
  .map(term => term.trim()).filter(Boolean);

export const rankingSearchTermsValid = (text = '') => {
  const terms = parseRankingSearchTerms(text);
  return terms.length <= 32 && terms.every(term => term.length <= 100
    && !Array.from(term).some((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127;
    }));
};

export const rankingWorkbenchRule = (model) => ({
  phase: 'index',
  kind: 'product_ranking',
  language_scope: 'neutral',
  document_type: 'product',
  ...(model.name.trim() ? { name: model.name.trim() } : {}),
  description: model.note.trim() || null,
  conditions: stripConditions(model.conditions),
  actions: [
    ...(Number(model.factor) !== 1
      ? [{ type: 'score.multiply', value: Number(model.factor) }] : []),
    ...(['boosted', 'mid'].flatMap((priority) => {
      const terms = parseRankingSearchTerms(model[`${priority}TermsText`] || '');
      return terms.length ? [{ type: 'search_terms.add', priority, terms }] : [];
    })),
  ],
  valid_from: model.validFrom || null,
  valid_to: model.validTo || null,
});

export const defaultConditionValue = (descriptor, operator) => {
  if (operator === 'exists') return true;
  if (descriptor?.value_type === 'boolean') return true;
  if (operator === 'between') return ['', ''];
  if (['in', 'not_in', 'contains_any', 'contains_all', 'contains_none',
    'text_contains_any', 'text_not_contains_any', 'text_contains_any_case'].includes(operator)) return [];
  if (descriptor?.value_type === 'number') return '';
  return '';
};

export const rankingConditionValid = (condition) => {
  if (!condition?.field || !condition?.operator) return false;
  if (['matches_regex', 'not_matches_regex'].includes(condition.operator)) {
    if (typeof condition.value !== 'string' || !condition.value.length
      || merchandisingRegexError(condition.value)) return false;
  }
  if (Array.isArray(condition.value)) {
    if (condition.operator === 'between') {
      return condition.value.length === 2
        && condition.value.every((value) => value !== '' && Number.isFinite(Number(value)))
        && Number(condition.value[0]) <= Number(condition.value[1]);
    }
    return condition.value.length > 0;
  }
  return condition.value !== '' && condition.value != null;
};

export const rankingExpressionValid = (group) => (
  Array.isArray(group?.items)
  && group.items.length > 0
  && group.items.every((item) => (
    Array.isArray(item.items)
      ? item.items.length >= 2 && rankingExpressionValid(item)
      : rankingConditionValid(item)
  ))
);

const removeFrom = (group, id) => {
  let removed = null;
  const items = [];
  group.items.forEach((item) => {
    if (item.__id === id) {
      removed = item;
      return;
    }
    if (Array.isArray(item.items)) {
      const nested = removeFrom(item, id);
      if (nested.removed) removed = nested.removed;
      if (nested.group.items.length === 1) items.push(nested.group.items[0]);
      else if (nested.group.items.length > 0) items.push(nested.group);
      return;
    }
    items.push(item);
  });
  return { removed, group: { ...group, items } };
};

const insertWithTarget = (group, source, targetId) => ({
  ...group,
  items: group.items.map((item) => {
    if (item.__id === targetId) {
      if (Array.isArray(item.items)) return { ...item, items: [...item.items, source] };
      return { __id: nextId(), match: 'any', items: [item, source] };
    }
    return Array.isArray(item.items) ? insertWithTarget(item, source, targetId) : item;
  }),
});

export const groupRankingConditions = (conditions, sourceId, targetId) => {
  if (!sourceId || sourceId === targetId) return conditions;
  const detached = removeFrom(conditions, sourceId);
  if (!detached.removed) return conditions;
  return insertWithTarget(detached.group, detached.removed, targetId);
};

export const removeRankingCondition = (conditions, id) => removeFrom(conditions, id).group;

export const updateRankingCondition = (group, id, update) => ({
  ...(group.__id === id ? { ...group, ...update } : group),
  items: group.items.map((item) => {
    if (item.__id === id) return { ...item, ...update };
    return Array.isArray(item.items) ? updateRankingCondition(item, id, update) : item;
  }),
});

export const ungroupRankingConditions = (group, id) => ({
  ...group,
  items: group.items.flatMap((item) => {
    if (item.__id === id && Array.isArray(item.items)) return item.items;
    return Array.isArray(item.items) ? [ungroupRankingConditions(item, id)] : [item];
  }),
});

export const appendRankingCondition = (group, condition) => ({
  ...group,
  items: [...group.items, { ...condition, __id: nextId() }],
});
